import * as Schema from "effect/Schema";
import {
  ActivityId,
  ConversationId,
  IsoDateTime,
  OperatorProjectId,
  RunId,
  PositiveInt,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

export const LearningKind = Schema.Literals(["knowledge", "skill", "environment"]);
export const LearningName = TrimmedNonEmptyString.check(
  Schema.isPattern(/^[a-zA-Z0-9][a-zA-Z0-9_:-]{0,127}$/u),
);
export const LearningSource = Schema.Struct({
  conversationId: ConversationId,
  runId: RunId,
  activityId: ActivityId,
  type: Schema.optionalKey(Schema.Literals(["human_instruction", "completed_work"])),
  evidenceIds: Schema.optionalKey(
    Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(16)),
  ),
  acceptedBy: Schema.optionalKey(TrimmedNonEmptyString),
  acceptedAt: Schema.optionalKey(IsoDateTime),
  verificationId: Schema.optionalKey(TrimmedNonEmptyString),
  previousRevision: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
  changeReason: Schema.optionalKey(TrimmedNonEmptyString.check(Schema.isMaxLength(600))),
});
export const LearningMetadata = Schema.Struct({
  kind: LearningKind,
  name: LearningName,
  title: TrimmedNonEmptyString.check(Schema.isMaxLength(240)),
  revision: TrimmedNonEmptyString,
  state: Schema.Literals(["confirmed", "needs_review", "removed"]),
  pinned: Schema.Boolean,
  replaces: Schema.optionalKey(LearningName),
  source: Schema.optionalKey(LearningSource),
  sourceUrls: Schema.optionalKey(
    Schema.Array(TrimmedNonEmptyString.check(Schema.isMaxLength(2048))).check(
      Schema.isMaxLength(8),
    ),
  ),
  reviewAfter: Schema.optionalKey(IsoDateTime),
  verifiedAt: Schema.optionalKey(IsoDateTime),
  updatedAt: IsoDateTime,
});
export type LearningMetadata = typeof LearningMetadata.Type;
export const LearningRevision = Schema.Struct({
  ...LearningMetadata.fields,
  source: Schema.optionalKey(
    Schema.Struct({
      ...LearningSource.fields,
      quote: TrimmedNonEmptyString.check(Schema.isMaxLength(4000)),
    }),
  ),
  content: Schema.String.check(Schema.isMaxLength(100_000)),
});
export type LearningRevision = typeof LearningRevision.Type;
/** Private records are never included in workspace snapshots. */
export const PersonalMemory = Schema.Struct({
  name: LearningName,
  title: TrimmedNonEmptyString.check(Schema.isMaxLength(240)),
  content: Schema.String.check(Schema.isMaxLength(4000)),
  projectId: Schema.optionalKey(OperatorProjectId),
  revision: TrimmedNonEmptyString,
  state: Schema.Literals(["confirmed", "removed"]),
  category: Schema.optionalKey(Schema.Literals(["profile", "preference", "priority"])),
  expiresAt: Schema.optionalKey(IsoDateTime),
  evidenceSequence: Schema.optionalKey(PositiveInt),
  source: LearningRevision.fields.source,
  updatedAt: IsoDateTime,
});
export type PersonalMemory = typeof PersonalMemory.Type;
export const LearningStatus = Schema.Struct({
  state: Schema.Literals(["pending", "running", "idle", "failed"]),
  message: Schema.optionalKey(Schema.String),
  updatedAt: IsoDateTime,
});
export type LearningStatus = typeof LearningStatus.Type;
const entry = { projectId: OperatorProjectId, kind: LearningKind, name: LearningName };
export const OperatorKnowledgeActionInput = Schema.Union([
  Schema.Struct({
    ...entry,
    action: Schema.Literal("read"),
    revision: Schema.optionalKey(TrimmedNonEmptyString),
  }),
  Schema.Struct({ ...entry, action: Schema.Literal("history") }),
  Schema.Struct({
    ...entry,
    action: Schema.Literals(["save", "propose"]),
    title: TrimmedNonEmptyString.check(Schema.isMaxLength(240)),
    content: TrimmedNonEmptyString.check(Schema.isMaxLength(100000)),
    pinned: Schema.Boolean,
    sourceUrls: LearningMetadata.fields.sourceUrls,
    reviewAfter: LearningMetadata.fields.reviewAfter,
    expectedRevision: Schema.NullOr(TrimmedNonEmptyString),
    expectedReplacementRevision: Schema.optionalKey(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    ...entry,
    action: Schema.Literal("remove"),
    expectedRevision: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    ...entry,
    action: Schema.Literal("restore"),
    revision: TrimmedNonEmptyString,
    expectedRevision: TrimmedNonEmptyString,
  }),
  Schema.Struct({ projectId: OperatorProjectId, action: Schema.Literal("retryLearning") }),
]);
export type OperatorKnowledgeActionInput = typeof OperatorKnowledgeActionInput.Type;
export const OperatorKnowledgeActionResult = Schema.Struct({
  entry: Schema.optionalKey(LearningRevision),
  replacementRevision: Schema.optionalKey(TrimmedNonEmptyString),
  history: Schema.optionalKey(Schema.Array(LearningMetadata)),
});
export type OperatorKnowledgeActionResult = typeof OperatorKnowledgeActionResult.Type;

const privateEntry = {
  projectId: OperatorProjectId,
  scope: Schema.Literals(["personal", "workspace"]),
  name: LearningName,
};
export const OperatorMemoryActionInput = Schema.Union([
  Schema.Struct({ projectId: OperatorProjectId, action: Schema.Literal("list") }),
  Schema.Struct({
    ...privateEntry,
    action: Schema.Literal("save"),
    title: PersonalMemory.fields.title,
    content: TrimmedNonEmptyString.check(Schema.isMaxLength(4000)),
    category: PersonalMemory.fields.category,
    expiresAt: PersonalMemory.fields.expiresAt,
    expectedRevision: Schema.NullOr(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    ...privateEntry,
    action: Schema.Literal("forget"),
    expectedRevision: TrimmedNonEmptyString,
  }),
]);
export type OperatorMemoryActionInput = typeof OperatorMemoryActionInput.Type;
export const OperatorMemoryActionResult = Schema.Struct({
  entry: Schema.optionalKey(PersonalMemory),
  entries: Schema.optionalKey(Schema.Array(PersonalMemory)),
});
export type OperatorMemoryActionResult = typeof OperatorMemoryActionResult.Type;

/** Agent-facing environment operations; the runtime supplies workspace scope. */
export const WorkspaceEnvironmentInput = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("list"),
    includeRemoved: Schema.optionalKey(Schema.Boolean),
  }),
  Schema.Struct({ action: Schema.Literals(["read", "history"]), name: LearningName }),
  Schema.Struct({
    action: Schema.Literal("save"),
    name: LearningName,
    title: LearningMetadata.fields.title,
    content: TrimmedNonEmptyString.check(Schema.isMaxLength(100_000)),
    expectedRevision: Schema.NullOr(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    action: Schema.Literal("remove"),
    name: LearningName,
    expectedRevision: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("restore"),
    name: LearningName,
    revision: TrimmedNonEmptyString,
    expectedRevision: TrimmedNonEmptyString,
  }),
]);
export type WorkspaceEnvironmentInput = typeof WorkspaceEnvironmentInput.Type;

/** The connected managed server owns one workspace; callers supply no project ID. */
export const OperatorEnvironmentActionInput = WorkspaceEnvironmentInput;
export type OperatorEnvironmentActionInput = typeof OperatorEnvironmentActionInput.Type;
export const OperatorEnvironmentActionResult = Schema.Struct({
  ...OperatorKnowledgeActionResult.fields,
  entries: Schema.optionalKey(Schema.Array(LearningMetadata)),
});
export type OperatorEnvironmentActionResult = typeof OperatorEnvironmentActionResult.Type;
